import React, { useState } from 'react';
import Header from './components/Header';
import BillScanner from './components/BillScanner';
import AttendeeManager from './components/AttendeeManager';
import ProFormaOrderer from './components/ProFormaOrderer';
import ItemAllocator from './components/ItemAllocator';
import BillSummary from './components/BillSummary';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [billItems, setBillItems] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [tipPercentage, setTipPercentage] = useState(10);
  const [calculations, setCalculations] = useState(null);
  const [isProForma, setIsProForma] = useState(false);

  const steps = isProForma ? [
    { id: 1, title: 'Pro Forma', description: 'Plan your order' },
    { id: 2, title: 'Add People', description: 'Enter attendee names' },
    { id: 3, title: 'Order Items', description: 'Select items for each person' },
    { id: 4, title: 'Review & Pay', description: 'Check totals and add tip' }
  ] : [
    { id: 1, title: 'Scan Bill', description: 'Upload and scan your receipt' },
    { id: 2, title: 'Add People', description: 'Enter attendee names' },
    { id: 3, title: 'Allocate Items', description: 'Assign items to people' },
    { id: 4, title: 'Review & Pay', description: 'Check totals and add tip' }
  ];

  const handleItemsScanned = (items) => {
    setBillItems(items);
    // If items array is empty, it means Pro Forma was selected
    if (items.length === 0) {
      setIsProForma(true);
    }
    setCurrentStep(2);
  };

  const handleAttendeesAdded = (attendeeList) => {
    setAttendees(attendeeList);
    setCurrentStep(3);
  };

  const handleProFormaItemsGenerated = (items) => {
    setBillItems(items);
    setCurrentStep(4); // Skip allocation step for Pro Forma
  };

  const handleItemsAllocated = (allocatedItems) => {
    setBillItems(allocatedItems);
    setCurrentStep(4);
  };

  const handleCalculateBill = (calcResults) => {
    setCalculations(calcResults);
  };

  const resetApp = () => {
    setCurrentStep(1);
    setBillItems([]);
    setAttendees([]);
    setCalculations(null);
    setIsProForma(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`
                  flex items-center justify-center w-10 h-10 rounded-full border-2
                  ${currentStep >= step.id 
                    ? 'bg-primary-600 border-primary-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-500'
                  }
                `}>
                  {step.id}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.id ? 'text-primary-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-400">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`hidden sm:block w-16 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-primary-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {currentStep === 1 && (
            <BillScanner onItemsScanned={handleItemsScanned} />
          )}
          
          {currentStep === 2 && (
            <AttendeeManager 
              attendees={attendees}
              onAttendeesAdded={handleAttendeesAdded}
              onBack={() => setCurrentStep(1)}
            />
          )}
          
          {currentStep === 3 && isProForma && (
            <ProFormaOrderer 
              attendees={attendees}
              onItemsGenerated={handleProFormaItemsGenerated}
              onBack={() => setCurrentStep(2)}
            />
          )}
          
          {currentStep === 3 && !isProForma && (
            <ItemAllocator 
              items={billItems}
              attendees={attendees}
              onItemsAllocated={handleItemsAllocated}
              onBack={() => setCurrentStep(2)}
            />
          )}
          
          {currentStep === 4 && (
            <BillSummary 
              items={billItems}
              attendees={attendees}
              tipPercentage={tipPercentage}
              onTipChange={setTipPercentage}
              onCalculate={handleCalculateBill}
              calculations={calculations}
              onBack={() => setCurrentStep(3)}
              onReset={resetApp}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
